package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.StyleRuleAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 screen 顶层 `styles { <规则>* }` 块。
 *
 * 每条规则：`<选择器> { name = value ... }`。
 *
 * 选择器语法（v1）：可选「类型」（IDENTIFIER，元素 kind，如 `button`）+ 零或多个 `.类名`。
 * 允许「纯类型」`button`、「纯 .类名」`.danger`、「类型 .类名」`button.primary`、多类名 `.danger.large`。
 * 本期不做后代组合子、不做 :state 伪类。
 *
 * 规则体内 `name = value` 与元素 prop 同样解析（value 走 ExpressionParser，
 * 复用 [ActionBlockParser.expressionParser]，支持 theme.X / 颜色函数 / 字面量）。
 *
 * 换行处理仿同包其它 parser（[ThemeBlockParser] / [ElementBlockParser]）。
 */
internal class StylesBlockParser(private val p: Parser) {

    /** 消费 `styles` IDENT + `{`，循环解析规则到 `}`，返回规则列表。 */
    fun parse(): List<StyleRuleAst> {
        // styles 是软关键字，词法层仍为 IDENTIFIER。
        p.expect(TokenType.IDENTIFIER, "styles 块缺少 'styles' 关键字")
        p.expect(TokenType.LBRACE, "styles 块缺少 '{'")
        p.skipNewlines()

        val rules = mutableListOf<StyleRuleAst>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            rules += parseRule()
            p.skipNewlines()
        }

        p.expect(TokenType.RBRACE, "styles 块缺少 '}'")
        return rules
    }

    /** 解析单条规则：先解析选择器，再解析 `{ name = value ... }` 规则体。 */
    private fun parseRule(): StyleRuleAst {
        val selectorPos = p.current().position

        // 选择器：可选类型（裸 IDENTIFIER）+ 零或多个 `.类名`。
        var typeKind: String? = null
        if (p.check(TokenType.IDENTIFIER)) {
            typeKind = p.advance().lexeme
        }
        val classes = mutableListOf<String>()
        while (p.check(TokenType.DOT)) {
            p.advance() // 消费 '.'
            if (!p.check(TokenType.IDENTIFIER)) {
                p.fail(DslError.E801_STYLE_SELECTOR_INVALID, selectorPos, "'.' 后缺少类名")
            }
            classes += p.advance().lexeme
        }

        // 至少要有类型或一个类名，否则选择器为空。
        if (typeKind == null && classes.isEmpty()) {
            p.fail(DslError.E801_STYLE_SELECTOR_INVALID, selectorPos, "缺少选择器（需类型或至少一个 .类名）")
        }

        // 规则体：`{ name = value ... }`
        p.expect(TokenType.LBRACE, "styles 规则选择器后缺少 '{'")
        p.skipNewlines()
        val props = mutableListOf<ElementAst.PropAssign>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            // prop 名允许 IDENTIFIER 及与字段语义重叠的关键字（如 color / string / number / bool），
            // 与 ElementBlockParser.isMemberHead 对齐，避免关键字保留干扰自然书写。
            if (!isPropNameHead(p.current().type)) {
                p.fail("styles 规则体内期望属性名，实际为 '${p.current().lexeme}'")
            }
            val nameTok = p.advance()
            p.expect(TokenType.EQUALS, "styles 规则属性 '${nameTok.lexeme}' 后缺少 '='")
            // value 解析方式与 ElementBlockParser 解析 prop value 完全一致。
            val expr = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
            props += ElementAst.PropAssign(nameTok.lexeme, expr, nameTok.position)
            if (!p.check(TokenType.RBRACE)) {
                if (!p.check(TokenType.NEWLINE) && !p.check(TokenType.SEMICOLON)) {
                    p.fail("styles 规则属性后缺少分隔符（NEWLINE 或 ';'），实际为 '${p.current().lexeme}'")
                }
                while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) {
                    p.advance()
                }
            }
        }
        val close = p.expect(TokenType.RBRACE, "styles 规则体缺少 '}'")

        return StyleRuleAst(typeKind, classes, props, p.completePosition(selectorPos, close))
    }

    /**
     * 允许用作 styles 规则体内 prop 名的 token 类型。
     *
     * 除普通 IDENTIFIER 外，显式放行与字段语义重叠的关键字（如 `color`），
     * 与 [ElementBlockParser] 的 `isMemberHead` 同一约定，避免关键字保留干扰自然书写。
     */
    private fun isPropNameHead(type: TokenType): Boolean =
        type == TokenType.IDENTIFIER ||
            type == TokenType.KW_COLOR ||
            type == TokenType.KW_STRING ||
            type == TokenType.KW_NUMBER ||
            type == TokenType.KW_BOOL
}
